import { useQuery } from '@tanstack/react-query';
import { MailCheck, MailX } from 'lucide-react';
import { useParams } from 'react-router-dom';

import { LoadingState } from '@/components/ui/states';
import { LoginRoute } from '@/routes/Login';
import { teamsApi } from '@/lib/api/endpoints';
import { useT } from '@/lib/i18n';

/**
 * The page an invitation link lands on, before anybody has signed in.
 *
 * Its job is to answer the question the recipient actually has — *what is
 * this, and is it still good?* — before asking them to sign in with Google.
 * Without it the link drops a stranger at a sign-in button that may refuse
 * them for a reason it cannot explain, which reads as a broken product rather
 * than an expired invitation.
 *
 * The address matters and is stated: the invitation is bound to it, so signing
 * in with a different Google account fails. Better said here than discovered.
 */
export function InviteRoute() {
  const t = useT();
  const { token = '' } = useParams();
  const { data, isPending, error } = useQuery({
    queryKey: ['invite', token],
    queryFn: () => teamsApi.previewInvitation(token),
    retry: false,
  });

  if (isPending) {
    return (
      <div className="flex h-full items-center justify-center">
        <LoadingState />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
        <MailX className="h-8 w-8 text-content-subtle" aria-hidden />
        <h1 className="text-lg font-semibold text-content">{t('invite.deadTitle')}</h1>
        <p className="max-w-sm text-sm text-content-muted">{t('invite.deadHint')}</p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col items-center justify-center gap-5 px-6 text-center">
      <MailCheck className="h-8 w-8 text-accent" aria-hidden />
      <div>
        <h1 className="text-lg font-semibold text-content">
          {data.team_name
            ? t('invite.welcomeTeam', { team: data.team_name })
            : t('invite.welcome')}
        </h1>
        <p className="mt-1.5 max-w-sm text-sm text-content-muted">
          {data.invited_by_name
            ? t('invite.invitedBy', { name: data.invited_by_name })
            : t('invite.invitedByUnknown')}
          {data.role === 'MANAGER' ? ` ${t('invite.asManagerNote')}` : ''}
        </p>
        <p className="mt-3 text-xs text-content-subtle">
          {t('invite.signInWith', { email: data.email })}
        </p>
      </div>

      {/* The sign-in screen itself, rather than a link to it: one page, one
          button, and the context above stays on screen while they use it. */}
      <div className="w-full max-w-sm">
        <LoginRoute />
      </div>
    </div>
  );
}
